/**
 * Stripe Connect — „Zahlungen empfangen".
 *
 * Verbindet das EIGENE Stripe-Konto des Händlers, damit Rechnungszahlungen aus
 * dem öffentlichen Portal direkt beim ausstellenden Händler eingehen (Destination-
 * Charge) statt auf einem zentralen Plattform-Konto. Funktioniert live erst mit
 * echten Stripe-Connect-Keys; mit dem Test-Proxy zeigt die Karte einen Hinweis.
 */
import { useEffect, useState, useCallback } from "react";
import { CreditCard, Loader2, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, Link2Off } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";

export function StripeConnectCard() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/users/me/stripe/connect/status");
            setStatus(data);
        } catch {
            setStatus({ available: false, connected: false });
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Rückkehr aus dem Stripe-Onboarding → Status frisch laden.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sc = params.get("stripe_connect");
        if (sc === "return" || sc === "refresh") {
            load();
            if (sc === "return") toast.success("Stripe-Onboarding abgeschlossen — Status wird aktualisiert.");
            params.delete("stripe_connect");
            const qs = params.toString();
            window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
        }
    }, [load]);

    const onboard = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/users/me/stripe/connect/onboard", { origin_url: window.location.origin });
            if (data?.url) { window.location.href = data.url; return; }
            toast.error("Kein Onboarding-Link erhalten.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Onboarding fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    const refresh = async () => { setBusy(true); await load(); setBusy(false); toast.success("Status aktualisiert."); };

    const disconnect = async () => {
        if (!window.confirm("Stripe-Konto wirklich trennen? Zahlungen gehen dann wieder aufs Plattform-Konto.")) return;
        setBusy(true);
        try {
            await api.post("/users/me/stripe/connect/disconnect");
            await load();
            toast.success("Stripe-Konto getrennt.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Trennen fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    if (status === null) return <div className="text-sm text-gray-400 inline-flex items-center gap-2" data-testid="stripe-connect-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>;

    return (
        <div className="space-y-4" data-testid="stripe-connect-card">
            <p className="text-xs text-gray-600">
                Verbinden Sie Ihr eigenes Stripe-Konto, damit Online-Zahlungen Ihrer Kunden
                (z. B. Bezahllink in der WhatsApp-Rechnung) <strong>direkt auf Ihr Konto</strong> eingehen —
                nicht über ein zentrales Sammelkonto.
            </p>

            {status.available === false && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-3 text-xs flex items-start gap-2" data-testid="stripe-connect-unavailable">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{status.reason || "Stripe Connect ist noch nicht aktiviert. Sobald die Live-Keys hinterlegt sind, können Sie Ihr Konto hier verbinden."}</span>
                </div>
            )}

            {status.available !== false && (
                <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-[#FFCC00]" />
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Verbindungs-Status</div>
                                <div className="font-bold text-sm" data-testid="stripe-connect-status">
                                    {status.connected
                                        ? (status.charges_enabled
                                            ? <Badge className="bg-emerald-100 text-emerald-800">Aktiv — Zahlungen gehen an Sie</Badge>
                                            : <Badge className="bg-amber-100 text-amber-800">Verbunden — Onboarding unvollständig</Badge>)
                                        : <Badge className="bg-gray-100 text-gray-700">Nicht verbunden</Badge>}
                                </div>
                            </div>
                        </div>
                        {status.connected && (
                            <div className="flex gap-3 text-[11px]">
                                <span className="inline-flex items-center gap-1" data-testid="stripe-connect-charges">
                                    {status.charges_enabled ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />} Zahlungen
                                </span>
                                <span className="inline-flex items-center gap-1" data-testid="stripe-connect-payouts">
                                    {status.payouts_enabled ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />} Auszahlungen
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {!status.connected || !status.charges_enabled ? (
                            <Button onClick={onboard} disabled={busy} className="bg-[#635BFF] hover:bg-[#5249e0] text-white font-bold rounded-sm uppercase tracking-wider" data-testid="stripe-connect-onboard">
                                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                                {status.connected ? "Onboarding fortsetzen" : "Mit Stripe verbinden"}
                            </Button>
                        ) : null}
                        {status.connected && (
                            <>
                                <Button onClick={refresh} disabled={busy} variant="outline" className="rounded-sm" data-testid="stripe-connect-refresh">
                                    <RefreshCw className="w-4 h-4 mr-2" /> Status aktualisieren
                                </Button>
                                <Button onClick={disconnect} disabled={busy} variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="stripe-connect-disconnect">
                                    <Link2Off className="w-4 h-4 mr-2" /> Trennen
                                </Button>
                            </>
                        )}
                    </div>
                    {status.account_id && (
                        <div className="text-[10px] text-gray-400 font-mono-num" data-testid="stripe-connect-account-id">Konto: {status.account_id}</div>
                    )}
                    {Number(status.platform_fee_percent) > 0 && (
                        <div className="text-[11px] text-gray-600" data-testid="stripe-connect-platform-fee">
                            Plattform-Gebühr: <b>{Number(status.platform_fee_percent).toLocaleString("de-DE")} %</b> je Online-Zahlung
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
